import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { AddressResponse, PersonSummaryResponse } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi, type ListPeopleParams } from '@/services/people.api';
import { Button, Spinner } from '@/components/ui';

interface Label {
  readonly personId: string;
  readonly displayName: string;
  readonly lines: readonly string[];
}

function addressLines(address: AddressResponse | null): readonly string[] {
  if (!address) return [];
  const cityLine = [address.city, address.region, address.postalCode]
    .filter((part) => part && part.trim().length > 0)
    .join(', ');
  return [address.line1, address.line2, cityLine, address.countryCode].filter(
    (line): line is string => Boolean(line && line.trim().length > 0),
  );
}

/** Bounded concurrency: a roster of a few hundred people should not open a
 *  few hundred simultaneous requests to fetch each one's address. */
async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  async function worker(): Promise<void> {
    for (;;) {
      const index = next++;
      if (index >= items.length) return;
      results[index] = await fn(items[index]!);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

/**
 * A standard 3-column mailing-label sheet (Avery 5160-shaped), reading
 * each person's address — a person with none on file is skipped, with a
 * visible count, so the operator is not left guessing why the sheet is
 * shorter than the roster.
 */
export function PrintLabelsPage() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [labels, setLabels] = useState<readonly Label[] | null>(null);
  const [skipped, setSkipped] = useState(0);

  const params: ListPeopleParams = {
    search: searchParams.get('search') || undefined,
    includeArchived: searchParams.get('includeArchived') === 'true',
    includeDeleted: searchParams.get('includeDeleted') === 'true',
    status: (searchParams.get('status') as ListPeopleParams['status']) || undefined,
    gender: searchParams.get('gender') || undefined,
    maritalStatus: searchParams.get('maritalStatus') || undefined,
    birthDateFrom: searchParams.get('birthDateFrom') || undefined,
    birthDateTo: searchParams.get('birthDateTo') || undefined,
  };
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    let cancelled = false;
    setLabels(null);
    setSkipped(0);

    async function build(): Promise<void> {
      const roster: PersonSummaryResponse[] = [];
      let cursor: string | undefined;
      for (;;) {
        const page = await peopleApi.list({ ...params, cursor, limit: 100 });
        roster.push(...page.data);
        if (!page.pagination.hasMore || !page.pagination.nextCursor) break;
        cursor = page.pagination.nextCursor;
      }

      const details = await mapWithConcurrency(roster, 8, (summary) => peopleApi.get(summary.id));

      if (cancelled) return;
      const built: Label[] = [];
      let missing = 0;
      for (const person of details) {
        const lines = addressLines(person.address);
        if (lines.length === 0) {
          missing += 1;
          continue;
        }
        built.push({ personId: person.id, displayName: person.displayName, lines });
      }
      setLabels(built);
      setSkipped(missing);
    }

    void build();
    return () => {
      cancelled = true;
    };
    // `paramsKey` is the intentional dependency — `params` is a fresh
    // object every render and would refetch on every keystroke elsewhere
    // in the tree.
  }, [paramsKey]);

  useEffect(() => {
    if (labels && labels.length > 0) window.print();
  }, [labels]);

  return (
    <div className="print-page">
      <div className="print-page-toolbar">
        <Link to="/people" className="back-link">
          {t('common.actions.back')}
        </Link>
        <Button type="button" variant="primary" onClick={() => window.print()}>
          {t('common.actions.print')}
        </Button>
      </div>

      <h1 className="print-page-title">{t('people.print.labelsTitle')}</h1>
      {labels && (
        <p className="print-page-meta">
          {t('people.print.labelsCount', { values: { count: labels.length } })}
          {skipped > 0 && ` · ${t('people.print.labelsSkipped', { values: { count: skipped } })}`}
        </p>
      )}

      {!labels && <Spinner label={t('common.states.loading')} />}

      {labels?.length === 0 && <p className="print-page-meta">{t('people.print.labelsEmpty')}</p>}

      {labels && labels.length > 0 && (
        <div className="print-labels-grid">
          {labels.map((label) => (
            <div key={label.personId} className="print-label">
              <p className="print-label-name">{label.displayName}</p>
              {label.lines.map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
